import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const EMPTY = {
    code: "", kind: "percent", value: 10, applies_to: "both",
    max_uses: 0, min_subtotal_cents: 0, expires_at: "", is_active: true,
};

export default function AdminDiscountCodes() {
    const [items, setItems] = useState([]);
    const [edit, setEdit] = useState(null);
    const load = () => api.get("/discount-codes").then((r) => setItems(r.data));
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        const payload = {
            ...edit,
            code: (edit.code || "").trim().toUpperCase(),
            value: parseFloat(edit.value) || 0,
            max_uses: parseInt(edit.max_uses, 10) || 0,
            min_subtotal_cents: parseInt(edit.min_subtotal_cents, 10) || 0,
            expires_at: edit.expires_at || null,
        };
        try {
            if (edit.id) await api.put(`/discount-codes/${edit.id}`, payload);
            else await api.post("/discount-codes", payload);
            toast.success("Saved");
            setEdit(null); load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        }
    };

    const del = async (c) => {
        if (!confirm(`Delete code ${c.code}?`)) return;
        await api.delete(`/discount-codes/${c.id}`);
        toast.success("Deleted"); load();
    };

    return (
        <div data-testid="admin-discount-codes">
            <div className="flex items-end justify-between mb-6">
                <div>
                    <div className="text-gold text-[10px] tracking-[0.4em] uppercase font-bold mb-2">Promotions</div>
                    <h1 className="h-display text-4xl">Discount Codes</h1>
                </div>
                <button type="button" onClick={() => setEdit({ ...EMPTY })} data-testid="add-code" className="btn-gold px-4 py-2 text-xs">+ New Code</button>
            </div>

            <div className="space-y-3">
                {items.map((c) => (
                    <div key={c.id} className="card-flat p-4 flex items-center gap-4" data-testid={`code-${c.id}`}>
                        <div className="flex-1 min-w-0">
                            <div className="font-mono text-gold font-bold text-lg">{c.code}</div>
                            <div className="text-xs text-ink mt-1">
                                {c.kind === "percent" ? `${c.value}% off` : `$${c.value.toFixed(2)} off`}
                                {" · "}applies to {c.applies_to}
                                {" · "}uses {c.uses_count}{c.max_uses ? ` / ${c.max_uses}` : " / ∞"}
                                {c.expires_at && ` · expires ${c.expires_at.slice(0,10)}`}
                                {!c.is_active && " · INACTIVE"}
                            </div>
                        </div>
                        <button type="button" onClick={() => setEdit({ ...c })} className="text-gold text-xs uppercase tracking-widest">Edit</button>
                        <button type="button" onClick={() => del(c)} className="text-red-400 text-xs uppercase tracking-widest">Delete</button>
                    </div>
                ))}
                {items.length === 0 && <div className="card-flat p-12 text-center text-ink">No codes yet.</div>}
            </div>

            {edit && (
                <div className="fixed inset-0 bg-black/80 grid place-items-center z-50 p-5" onClick={(e) => e.target === e.currentTarget && setEdit(null)}>
                    <form onSubmit={save} className="bg-bg-soft border border-gold/40 max-w-xl w-full p-6 space-y-3" data-testid="code-edit-modal">
                        <h2 className="h-display text-2xl mb-2">{edit.id ? "Edit Code" : "New Code"}</h2>
                        <F label="Code (e.g. KINGS10)" value={edit.code} onChange={(v) => setEdit({ ...edit, code: v.toUpperCase() })} />
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-[10px] tracking-[0.3em] uppercase text-ink font-bold mb-2">Kind</label>
                                <select value={edit.kind} onChange={(e) => setEdit({ ...edit, kind: e.target.value })} className="w-full px-3 py-2 text-sm bg-bg border border-line focus:border-gold outline-none">
                                    <option value="percent">Percent %</option>
                                    <option value="fixed">Fixed $ off</option>
                                </select>
                            </div>
                            <F label={edit.kind === "percent" ? "Percent (e.g. 10)" : "Dollars off (e.g. 5)"} type="number" value={edit.value} onChange={(v) => setEdit({ ...edit, value: v })} />
                        </div>
                        <div>
                            <label className="block text-[10px] tracking-[0.3em] uppercase text-ink font-bold mb-2">Applies To</label>
                            <select value={edit.applies_to} onChange={(e) => setEdit({ ...edit, applies_to: e.target.value })} className="w-full px-3 py-2 text-sm bg-bg border border-line focus:border-gold outline-none">
                                <option value="both">Both (tickets + store)</option>
                                <option value="store">Store only</option>
                                <option value="tickets">Tickets only</option>
                            </select>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <F label="Max Uses (0 = unlimited)" type="number" value={edit.max_uses} onChange={(v) => setEdit({ ...edit, max_uses: v })} />
                            <F label="Min Subtotal (cents)" type="number" value={edit.min_subtotal_cents} onChange={(v) => setEdit({ ...edit, min_subtotal_cents: v })} />
                        </div>
                        <F label="Expires At (YYYY-MM-DD, optional)" value={edit.expires_at || ""} onChange={(v) => setEdit({ ...edit, expires_at: v })} />
                        <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={edit.is_active} onChange={(e) => setEdit({ ...edit, is_active: e.target.checked })} /> Active</label>
                        <div className="flex justify-end gap-2 pt-3">
                            <button type="button" onClick={() => setEdit(null)} className="px-4 py-2 text-xs text-ink hover:text-white">Cancel</button>
                            <button type="submit" className="btn-gold px-5 py-2 text-xs">Save</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}

function F({ label, value, onChange, type = "text" }) {
    return (
        <div>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-ink font-bold mb-2">{label}</label>
            <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 text-sm bg-bg border border-line focus:border-gold outline-none" />
        </div>
    );
}
